import React, { useCallback, useContext, useEffect } from "react";

import {
  CommandSource,
  CountryCode,
  Emptyable,
  FormType,
  PaymentMethod,
  convertCountryCodeToCountry,
} from "@simplyk/common";
import { useElements } from "@stripe/react-stripe-js";
import { inferRouterOutputs } from "@trpc/server";
import { useSearchParams } from "next/navigation";
import { useSnackbar } from "notistack";
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormHandleSubmit,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";

import { AmplitudeEvents } from "../../constants/amplitude";
import { FrontendTicketingContext } from "../../contexts/FrontendTicketingContext";
import { GetCurrentUserFrontendObject } from "../../contexts/types";
import { getThankYouPageUrl } from "../../helpers/thankYouPage";
import { runWithMinimumDuration } from "../../helpers/time";
import { AppRouterType, trpc } from "../../helpers/trpc";
import { useAmplitude } from "../../hooks/amplitude/useAmplitude";
import { useCreateRegistrationCampaigns } from "../../hooks/useCreateRegistrationCampaigns";
import { useIsEmbeddedModal } from "../../hooks/useEmbeddedModal";
import { useTranslate } from "../../hooks/useTranslate";
import { DonationFormPaymentInput } from "../../types/donationForm";
import { getAnimationNumber, TIME_BETWEEN_STEPS } from "../Animations/ThankYouLoader/ThankYouLoader";
import { SubmitCommandFormInput } from "../PaymentProcessor/helper";

import { HandleErrorParams, unknownPaymentErrorCode } from "./helper";
import { usePaymentErrorContext } from "./PaymentErrorContext";
import { useAmplitudePaymentEventLog } from "./useAmplitudePaymentEventLog";
import { useCashapp } from "./useCashapp";
import { useOfflinePayment } from "./useOfflinePayment";
import { usePad } from "./usePad";
import { useStripePayment } from "./useStripePayment";

import { useDurationOnPage } from "@/hooks/useDurationOnPage";
import { FrontendFormContext } from "@/src/contexts/FrontendFormContext";
import { useLocaleContext } from "@/src/contexts/LocaleContext";
import { SessionContext } from "@/src/contexts/SessionContext";
import { TicketingPaymentInput } from "@/types/ticketing";
import { DiscountOutput, DonationFormFrontendPageOutput, TicketingFrontendPageOutput } from "@/types/trpc";

type SubmitCommandOutput = inferRouterOutputs<AppRouterType>["form_submitCommand"];

export type Props<T extends FieldValues> = {
  handleSubmit: UseFormHandleSubmit<TicketingPaymentInput | DonationFormPaymentInput>;
  formObject: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  setValue: UseFormSetValue<DonationFormPaymentInput | TicketingPaymentInput>;
  trigger: UseFormTrigger<T>;
  control: Control<T>;
  errors: FieldErrors<T>;
  validDiscount?: Emptyable<DiscountOutput>;
};

export function usePaymentSubmit<T extends FieldValues>({
  handleSubmit,
  formObject,
  validDiscount,
  setValue,
  errors,
}: Props<T>) {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslate();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const { event: ticketingEvent } = useContext(FrontendTicketingContext);
  const { sessionId } = useContext(SessionContext);

  const {
    commandId,
    organization,
    formType,
    isArchived,
    tip,
    selectedPaymentMethod,
    category,
    formData,
    postTransactionUrl,
    prioritizeBankPayment,
    setIsSubmitting,
    paymentMode,
    selectedTip,
    shouldSendTipPercentage,
    isAuction,
    generateETicket,
    themeColor,
    setIsPadLoading,
  } = useContext(FrontendFormContext);
  const isEmbeddedModal = useIsEmbeddedModal(query);
  const { locale, isoLocale } = useLocaleContext();
  const { logAmplitudeEvent } = useAmplitude();
  const { getDurationTimeOnPage } = useDurationOnPage();
  const {
    shouldCreateCampaigns,
    createCampaigns,
    shouldNotCreateCampaignButRedirectToPeerToPeer,
    createPeerToPeerV2RedirectUrl,
  } = useCreateRegistrationCampaigns();
  const elements = useElements();
  const { logDonorFormSubmitted, logFormClickSubmitPaymentError } = useAmplitudePaymentEventLog({
    errors,
    formObject,
  });
  const { mutateAsync: reOpenCommandMutation } = trpc.reOpenCommand.useMutation();

  useEffect(() => {
    if (prioritizeBankPayment) {
      logAmplitudeEvent(AmplitudeEvents.FormInsertAmountZeffyModelMessage, {
        abTestMessage: "positive",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prioritizeBankPayment]);

  const getFormErrors = useCallback(async (): Promise<string | null> => {
    if (isArchived) {
      return t("common", "form_archived");
    }

    if (organization?.blockPayment) {
      return t("common", "payment_blocked");
    }

    if (!organization.isStripeCustomAccountActive) {
      return t("common", "bankIsNotConnectedError");
    }

    return null;
  }, [isArchived, organization?.blockPayment, organization.isStripeCustomAccountActive, t]);

  const getThankYouUrl = useCallback(
    ({
      command,
      formType,
      organization,
    }: {
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      formType: FormType;
      organization: NonNullable<GetCurrentUserFrontendObject["currentOrganization"]>;
    }): string => {
      return getThankYouPageUrl({
        command: command as NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"],
        formType,
        organization,
        postTransactionUrl,
        isEmbeddedModal,
        generateETicket,
        event: ticketingEvent,
        formData,
        isoLocale,
        category,
        isAuction,
        themeColor,
        isDarkMode: formObject.isDarkMode || false,
        hasShapes: formObject.hasShapes || true,
      });
    },
    [
      postTransactionUrl,
      isEmbeddedModal,
      generateETicket,
      ticketingEvent,
      formData,
      isoLocale,
      category,
      isAuction,
      themeColor,
      formObject.isDarkMode,
      formObject.hasShapes,
    ]
  );

  const { setPaymentError } = usePaymentErrorContext();
  const handlePaymentError = useCallback(
    ({ message, commandId, metadata, isPaymentSucceeded }: HandleErrorParams) => {
      logDonorFormSubmitted({
        isExpressCheckout: metadata?.isExpressCheckout as boolean,
        hasSucceeded: Boolean(isPaymentSucceeded),
        formErrors: [(metadata?.code as string) || "unknownError"],
      });
      logFormClickSubmitPaymentError(metadata);

      if (isPaymentSucceeded) {
        enqueueSnackbar(t("common", "paymentSucceededWithErrorsMessage") + " " + message, {
          vibe: "positive",
          persist: true,
        });
      } else {
        setPaymentError(message || t("common", "unknown_error", { code: unknownPaymentErrorCode.other1.code }));
      }

      if (commandId && !isPaymentSucceeded) {
        reOpenCommandMutation({ commandId });
      }

      setIsSubmitting(false);
      setIsPadLoading(false);
    },
    [
      enqueueSnackbar,
      logDonorFormSubmitted,
      logFormClickSubmitPaymentError,
      reOpenCommandMutation,
      setIsPadLoading,
      setIsSubmitting,
      setPaymentError,
      t,
    ]
  );

  const handlePostSubmit = useCallback(
    async ({
      paymentInput,
      command,
      formType,
      isExpressCheckout,
    }: {
      paymentInput: TicketingPaymentInput | DonationFormPaymentInput;
      command?: NonNullable<NonNullable<SubmitCommandOutput>["data"]>["command"];
      formType: FormType;
      isExpressCheckout?: boolean;
    }): Promise<{ returnUrl?: string | null; error?: string | null }> => {
      logDonorFormSubmitted({
        isExpressCheckout: Boolean(isExpressCheckout),
        hasSucceeded: true,
      });
      // a user will enter that flows if he/she is paying a registration form v1
      if (shouldCreateCampaigns(paymentInput, formType)) {
        const result = await createCampaigns({
          firstName: command?.firstName,
          lastName: command?.lastName,
          email: command?.email,
          paymentInput,
        });

        if (result.returnUrl) {
          return { returnUrl: result.returnUrl };
        }
        return { error: result.error };
      }

      // a user will enter that flows if he/she is paying a registration form v2
      if (shouldNotCreateCampaignButRedirectToPeerToPeer(paymentInput, formType)) {
        const result = await createPeerToPeerV2RedirectUrl({
          firstName: command?.firstName,
          lastName: command?.lastName,
          email: command?.email,
          paymentInput,
          commandId: command?.id,
        });
        if (result.returnUrl) {
          return { returnUrl: result.returnUrl };
        }
        return { error: result.error };
      }
      const thankYouUrl = getThankYouUrl({ command, formType, organization });

      const returnUrl = postTransactionUrl || thankYouUrl;

      return { returnUrl };
    },
    [
      createCampaigns,
      createPeerToPeerV2RedirectUrl,
      getThankYouUrl,
      logDonorFormSubmitted,
      organization,
      postTransactionUrl,
      shouldCreateCampaigns,
      shouldNotCreateCampaignButRedirectToPeerToPeer,
    ]
  );

  const { payByPad } = usePad({ setValue, handleError: handlePaymentError, handlePostSubmit });
  const { payByCashapp } = useCashapp({ setValue, handleError: handlePaymentError, getThankYouUrl });
  const { payOffline } = useOfflinePayment({ setValue, handleError: handlePaymentError, handlePostSubmit });
  const { payByStripe } = useStripePayment({ setValue, handleError: handlePaymentError, handlePostSubmit });

  const onInvalid = useCallback(
    async (isExpressCheckout: boolean) => {
      const addressElement = elements?.getElement?.("address");
      await addressElement?.getValue?.();
      await elements?.submit();
      logDonorFormSubmitted({
        isExpressCheckout,
        hasSucceeded: false,
      });
    },
    [elements, logDonorFormSubmitted]
  );

  const handleFormSubmit = useCallback(
    (isExpressCheckout = false) =>
      async (event?: React.SyntheticEvent) => {
        event && event.preventDefault();

        handleSubmit(
          async (paymentInput) => {
            if (selectedPaymentMethod !== PaymentMethod.Pad) {
              setIsSubmitting(true);
            } else {
              setIsPadLoading(true);
            }

            const minimumDuration =
              selectedPaymentMethod !== PaymentMethod.CashApp
                ? TIME_BETWEEN_STEPS * 1000 * getAnimationNumber({ isFree: false })
                : 0;
            const url = await runWithMinimumDuration<Emptyable<string>>(async () => {
              const formError = await getFormErrors();
              if (formError) {
                handlePaymentError({ message: formError });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              const addressElement = elements?.getElement?.("address");
              const addressValue = await addressElement?.getValue?.();
              if (addressValue) {
                const { city, country, line1, line2, postal_code, state } = addressValue.value.address;
                paymentInput.address = line2 ? `${line1} ${line2}` : line1;
                paymentInput.city = city;
                paymentInput.postalCode = postal_code;
                paymentInput.region = state;
                paymentInput.country = convertCountryCodeToCountry(country as CountryCode);
                paymentInput.firstName = addressValue.value.firstName;
                paymentInput.lastName = addressValue.value.lastName;
              }

              if ((tip && tip < 0) || !organization?.country || !formType) {
                handlePaymentError({
                  message: t("common", "unknown_error", { code: unknownPaymentErrorCode.other2.code }),
                });
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              const submitCommandInput: SubmitCommandFormInput = {
                paymentInput,
                paymentMethod: selectedPaymentMethod,
                source: CommandSource.FormSubmission,
                formObject,
                user: null,
                contact: null,
                formType,
                validDiscount: validDiscount || null,
                session: { sessionId, durationOnPage: getDurationTimeOnPage() },
                routerQuery: query,
                commandId,
                locale,
                tip: (!shouldSendTipPercentage ? tip : 0) || 0,
                tipPercentage: (shouldSendTipPercentage ? selectedTip.percentage : 0) || 0,
                stripePaymentMethodId: null,
                isExpressCheckout,
              };

              if (selectedPaymentMethod === PaymentMethod.Pad) {
                return {
                  callbackResult: await payByPad(submitCommandInput, paymentMode, minimumDuration),
                  shouldAwaitMinimumDuration: false,
                };
              }
              if (selectedPaymentMethod === PaymentMethod.CashApp) {
                payByCashapp(submitCommandInput, paymentMode);
                return { callbackResult: null, shouldAwaitMinimumDuration: false };
              }

              if (selectedPaymentMethod === PaymentMethod.Cheque || selectedPaymentMethod === PaymentMethod.Cash) {
                return { callbackResult: await payOffline(submitCommandInput), shouldAwaitMinimumDuration: true };
              }

              return { callbackResult: await payByStripe(submitCommandInput), shouldAwaitMinimumDuration: true };
            }, minimumDuration);
            if (url) {
              window.location.href = url;
            }
          },
          () => onInvalid(isExpressCheckout)
        )().finally(() => {
          setIsSubmitting(false);
          setIsPadLoading(false);
        });
      },
    [
      handleSubmit,
      selectedPaymentMethod,
      setIsSubmitting,
      setIsPadLoading,
      getFormErrors,
      elements,
      tip,
      organization?.country,
      formType,
      formObject,
      validDiscount,
      sessionId,
      getDurationTimeOnPage,
      query,
      commandId,
      locale,
      shouldSendTipPercentage,
      selectedTip.percentage,
      payByStripe,
      handlePaymentError,
      t,
      payByPad,
      paymentMode,
      payByCashapp,
      payOffline,
      onInvalid,
    ]
  );

  return {
    handleFormSubmit,
    handlePaymentError,
  };
}
